import  { useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import api from "../../../utils/redux/api";
import { toast } from "react-toastify";
import { Box, Typography } from "@mui/material";
import axiosServicesOnboarding from "../../../services/axiosServicesOnboarding";

const StepBox = ({ number, title }) => {
  return (
    <Box position="relative">
      <Box
        display="flex"
        alignItems="center"
        border="1px solid #e0e0e0"
        borderRadius="8px"
        padding="6px"
        bgcolor="#ffffff"
        position="relative"
      >
        {/* Number Circle */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="40px"
          height="40px"
          border="2px solid #001d7e"
          borderRadius="50%"
          marginRight="8px"
        >
          <Typography variant="body1" color="#001d7e" fontWeight="bold">
            {number}
          </Typography>
        </Box>

        {/* Title */}
        <Typography variant="body1" fontWeight="bold" color="#001d7e">
          {title}
        </Typography>

        {/* Pointed edge - border */}
        <Box
          position="absolute"
          right="-21px"
          top="50%"
          sx={{
            transform: "translateY(-50%)",
            borderLeft: "21px solid #e0e0e0",
            borderTop: "21px solid transparent",
            borderBottom: "21px solid transparent",
          }}
        />

        {/* Pointed edge - fill */}
        <Box
          position="absolute"
          right="-20px"
          top="50%"
          sx={{
            transform: "translateY(-50%)",
            borderLeft: "20px solid #ffffff",
            borderTop: "20px solid transparent",
            borderBottom: "20px solid transparent",
            zIndex: 1,
            height: "60px",
          }}
        />
      </Box>
    </Box>
  );
};

const EmailVerification = ({ onBack, data, refresh, setRefresh }) => {
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const uniqueNumbersRef = useRef(null); // To persist unique numbers
  const [reSendOtpDetails, setReSendOtpDetails] = useState(false);
  const [reSendOtpLoading, setReSendOtpLoading] = useState(false);

  // Generate unique numbers once
  const generateUniqueNumbers = () => {
    if (!uniqueNumbersRef.current) {
      const uniqueSet = new Set();
      while (uniqueSet.size < 10) {
        const timestamp = Date.now(); // Current timestamp in milliseconds
        const randomNum = Math.floor(1000 + Math.random() * 9000); // 4-digit random number
        uniqueSet.add(`${timestamp}${randomNum}`);
      }
      uniqueNumbersRef.current = Array.from(uniqueSet);
    }
    return uniqueNumbersRef.current;
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    otp: otpSent
      ? Yup.string()
          .required("OTP is required")
          .length(6, "OTP must be exactly 6 digits")
      : Yup.string(),
  });

  const handleApiCall = async (values) => {
    // const uniqueNumbers = generateUniqueNumbers(10); // Generate one unique number
    const uniqueNumbers = generateUniqueNumbers(); // Get unique numbers

    const body = {
      EmailPhoneNumber: values.email,
      otpChannel: "Email",
      purpose: "EmailVerification",
      requestId: uniqueNumbers[0],
      phoneNumber: null,
      userId: data?.id,
    };

    setReSendOtpDetails(body);

    try {
      setLoading(true);
      setError("");

      if (!otpSent) {
        // Send OTP
        const res = await api.post("SecurityService/generate-otp", body);
        console.log("OTP sent response:", res);
        setOtpSent(true);
      } else {
        // Validate OTP - use the most recent requestId from reSendOtpDetails
        const currentRequestId = reSendOtpDetails?.requestId || uniqueNumbers[0];
        const res = await axiosServicesOnboarding.post("customer/validate-otp", {
          otp: values.otp,
          requestId: currentRequestId,
        });
        console.log("OTP validation response:", res);
        toast.success(res?.data?.message || "Email verified successfully!");
        setRefresh(!refresh); // Refresh the parent component
        onBack();
      }
    } catch (error) {
      console.error("API error:", error);
      setError(
        error?.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setReSendOtpLoading(true);
      setError("");
      
      // Generate a new requestId each time
      const timestamp = Date.now();
      const randomNum = Math.floor(1000 + Math.random() * 9000);
      const newRequestId = `${timestamp}${randomNum}`;
      
      const body = {
        ...reSendOtpDetails,
        requestId: newRequestId,
      };
      
      const res = await api.post("SecurityService/generate-otp", body);
      console.log("OTP sent response:", res);
      toast.success(res?.data?.message || "OTP sent successfully!");
      // alert("OTP sent successfully!");
      
      // Update reSendOtpDetails with the new requestId for future resends
      setReSendOtpDetails(body);
    } catch (error) {
      console.error("API error:", error);
      setError(
        error?.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setReSendOtpLoading(false);
    }
  };

  return (
    <div className="w-full md:w-[650px] mx-auto p-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2 text-gray-700 text-lg font-semibold">
          {/* <FaUser className="text-xl text-primary-color" /> */}
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
              stroke="#0E156F"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
              stroke="#0E156F"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          <span>Verify Email Address</span>
        </div>
        <button onClick={onBack} className="text-gray-600 text-2xl">
          {/* <IoCloseOutline /> */}
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.0003 29.3337C23.3641 29.3337 29.3337 23.3641 29.3337 16.0003C29.3337 8.63653 23.3641 2.66699 16.0003 2.66699C8.63653 2.66699 2.66699 8.63653 2.66699 16.0003C2.66699 23.3641 8.63653 29.3337 16.0003 29.3337Z"
              stroke="#25233A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M20 12L12 20"
              stroke="#25233A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 12L20 20"
              stroke="#25233A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="px-8 py-2 bg-[#edf1f9] rounded-md mb-8">
        <Box maxWidth="600px" width="100%" p={2}>
          <StepBox number="01" title="Verify Email" />
        </Box>
      </div>

      <h3 className="text-lg font-semibold text-primary-color text-center mb-6">
        Email Verification
      </h3>

      <Formik
        initialValues={{ email: data?.emailAddress, otp: "" }}
        validationSchema={validationSchema}
        onSubmit={handleApiCall}
      >
        {({ isSubmitting }) => (
          <Form className="space-y-6 w-full md:w-[400px] mx-auto">
            <div>
              <label className="block text-gray-600 font-medium mb-2">
                Registered email address <span className="text-red-500">*</span>
              </label>
              <Field
                type="email"
                name="email"
                placeholder="Enter your email"
                className="w-full p-3 border border-gray-300 rounded-md"
                disabled={otpSent}
              />
              <ErrorMessage
                name="email"
                component="div"
                className="text-red-500 text-sm mt-1"
              />
            </div>

            {!otpSent && (
              <div className="text-[13px] text-[#171832] text-center w-full mx-auto flex items-center justify-center">
                <p className="w-[225px] text-[#171832]">
                  A confirmation code will be sent to the provided email.
                </p>
              </div>
            )}

            {otpSent && (
              <div>
                <label className="block text-gray-600 font-medium mb-2">
                  Enter OTP:
                </label>
                <Field
                  type="text"
                  name="otp"
                  placeholder="Enter OTP"
                  className="w-full p-3 border border-gray-300 rounded-md"
                />
                <ErrorMessage
                  name="otp"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-color hover:bg-[#8388B6] text-white font-semibold py-4 px-6 rounded-md"
            >
              {loading ? "Loading..." : otpSent ? "Activate" : "Send OTP"}
            </button>

            {otpSent && (
              <p className="text-sm text-gray-500 mt-2 text-center">
                Didn’t get OTP?{" "}
                <span
                  className="text-primary-color cursor-pointer"
                  onClick={() => handleResendOtp()}
                >
                  {reSendOtpLoading ? "Resending..." : "Resend"}
                </span>
              </p>
            )}

            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default EmailVerification;
